/**
 * The client workspace store — PLAN §8.3's "client store" for M2.
 *
 * ```
 * UI action → store.dispatch(type, payload)
 *           → makeCommand(…, { commandId, timestamp, actor })   ← the clock lives here
 *           → applyCommand(state, command)                      ← the pure reducer
 *           → notify subscribers                                ← useSyncExternalStore
 *           → onChange(state)                                   ← debounced IndexedDB write
 * ```
 *
 * Three things are deliberately *not* here:
 *
 * - **No reducer logic.** Every mutation is a command from `@beerwright/commands`;
 *   this class only supplies the envelope the pure packages refuse to invent
 *   (ids and timestamps) and holds the result.
 * - **No React.** The store is a plain object with `subscribe`/`getState`, so it
 *   is testable without a renderer.
 * - **No persistence.** It emits `onChange`; `persistence.ts` decides what that
 *   costs.
 *
 * ## Undo
 *
 * `invert(state, command)` is computed against the **pre**-state and stacked
 * alongside the command. Undo applies the inverse; redo re-applies the original
 * and re-inverts against the state it actually saw. The commands package
 * guarantees `apply(invert(s, c), apply(s, c)) === s`, so an undo is exact,
 * modification times included — which is why the inverse's envelope timestamp
 * is passed through untouched and only its `commandId` is refreshed.
 *
 * Stacks are in-memory: a reload starts a fresh history, which matches every
 * other editor and keeps the persisted snapshot a plain document.
 *
 * ## Undo across somebody else's edit
 *
 * Remote state arrives through `replaceState`, and by default that clears both
 * stacks: an inverse computed against a state that no longer exists is exactly
 * what `invert`'s pre-state contract forbids applying. The one exception is a
 * replace that says *which commands* made the difference (`over`), which is
 * what the sync client passes for another device's or another member's delta
 * (PLAN-COLLAB D-CO10). Then an entry is kept exactly when its fields and those
 * commands' fields do not meet (`commandsOverlap`): the part of the state its
 * inverse writes is still the part it was computed against. Too broad a scope
 * costs an entry; that is the direction the scopes err in.
 *
 * Each entry carries an id, stable across undo and redo, so the composite store
 * a shared recipe's editor reads (D-CO11a) can keep one ordered stack of
 * `(source, entryId)` pairs and skip a pair whose entry was dropped here.
 *
 * ## Sync (M3)
 *
 * `observeCommands` is the seam the sync engine attaches to. Every command the
 * store *applies locally* is announced there — dispatches, and the inverse an
 * undo applies, and the re-application a redo does, because all three are real
 * mutations the server has to learn about. `replaceState` announces nothing: it
 * is how remote state arrives, and echoing it back would be a loop.
 */

import type {
  Command,
  CommandError,
  CommandOf,
  CommandType,
  WorkspaceState,
} from '@beerwright/commands'; // prettier-ignore
import {
  applyCommand,
  commandsOverlap,
  describeCommand,
  emptyWorkspace,
  invert,
  makeCommand,
} from '@beerwright/commands'; // prettier-ignore
import { newId } from '@/lib/ids';

/** What a dispatch returns. Rejections are typed, never thrown. */
export type DispatchResult =
  | {
      readonly ok: true;
      readonly state: WorkspaceState;
      /** The history entry this stacked, or `null` when nothing was stacked. */
      readonly entryId: string | null;
    }
  | { readonly ok: false; readonly error: CommandError };

/**
 * A rule outside the reducer that may refuse a command before it is applied.
 *
 * One thing installs one: {@link WorkspaceStore.setGuard}, from the workspace
 * binding, with the plan caps, and only while nobody is signed in — a guest has
 * no Durable Object to refuse anything, and without this the free tier's ten
 * recipes were a number the sidebar displayed rather than a limit (see
 * `lib/plan-cap.ts` and `store/workspace-context.tsx`).
 *
 * It is handed the state the command would *actually* see — the batch's running
 * projection, not the committed state — so ten creates in one import are judged
 * against a count that grows as they land.
 */
export type DispatchGuard = (state: WorkspaceState, command: Command) => CommandError | null;

/**
 * A type+payload pair, before the store stamps an envelope.
 *
 * Heterogeneous batches (rewriting a malt bill from percentages, an accepted
 * assistant proposal) are arrays of these. `dispatch` is the single-step
 * special case.
 */
export type DispatchStep = {
  [K in CommandType]: { readonly type: K; readonly payload: CommandOf<K>['payload'] };
}[CommandType];

/**
 * One entry of history: the commands, and the commands that undo them.
 *
 * Arrays rather than a single pair because an accepted assistant proposal is
 * *one* thing the user did (PLAN §7.1) even when it is five commands. Undoing it
 * with five ⌘Zs would be a leak of the transport into the interaction. A plain
 * dispatch is simply a batch of one, so the single-command path is unchanged.
 */
interface HistoryEntry {
  /**
   * Stable for the entry's life, across undo and redo alike, and unique within
   * this store. Minted by a counter of its own rather than by `nextCommandId`:
   * an entry is not a command, and spending the command-id sequence on it
   * would shift every id a test pins and count as a command where tests count
   * commands.
   */
  readonly id: string;
  readonly commands: readonly Command[];
  /** Inverses in **application order**; undo replays them backwards. */
  readonly inverses: readonly Command[];
  /** One line of English, computed against the pre-state, for the undo tooltip. */
  readonly summary: string;
}

/** Everything the store publishes about its history, in one snapshot-stable object. */
export interface HistorySnapshot {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  /** What ⌘Z would undo, or `null`. */
  readonly undoLabel: string | null;
  /** What ⇧⌘Z would redo, or `null`. */
  readonly redoLabel: string | null;
}

/** Construction options. All optional; the defaults are the browser's. */
export interface WorkspaceStoreOptions {
  readonly initialState?: WorkspaceState;
  /** Wall clock. Injected so tests can pin timestamps. */
  readonly now?: () => number;
  /** Command id source. Injected so tests can pin ids. */
  readonly nextCommandId?: () => string;
  /** How deep the undo history goes before the oldest entry is dropped. */
  readonly historyLimit?: number;
}

const DEFAULT_HISTORY_LIMIT = 200;

const EMPTY_HISTORY: HistorySnapshot = {
  canUndo: false,
  canRedo: false,
  undoLabel: null,
  redoLabel: null,
};

export class WorkspaceStore {
  #state: WorkspaceState;
  #history: HistorySnapshot = EMPTY_HISTORY;
  readonly #listeners = new Set<() => void>();
  readonly #commandListeners = new Set<(command: Command) => void>();
  readonly #undo: HistoryEntry[] = [];
  readonly #redo: HistoryEntry[] = [];
  readonly #now: () => number;
  readonly #nextCommandId: () => string;
  readonly #historyLimit: number;
  #guard: DispatchGuard | null = null;
  #entrySequence = 0;

  constructor(options: WorkspaceStoreOptions = {}) {
    this.#state = options.initialState ?? emptyWorkspace();
    this.#now = options.now ?? Date.now;
    this.#nextCommandId = options.nextCommandId ?? (() => newId('c'));
    this.#historyLimit = options.historyLimit ?? DEFAULT_HISTORY_LIMIT;
  }

  /** The current workspace. Immutable — never mutate the returned object. */
  getState = (): WorkspaceState => this.#state;

  /** Undo/redo availability, as one referentially stable object. */
  getHistory = (): HistorySnapshot => this.#history;

  /**
   * Subscribe to state changes.
   *
   * Bound as a field rather than a method so it can be handed straight to
   * `useSyncExternalStore` without a wrapper closure (a new function identity
   * there resubscribes on every render).
   */
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  /**
   * Subscribe to commands this store applied locally.
   *
   * The sync engine's entry point: the listener is called after the state has
   * been committed, with the exact envelope that produced it, so appending it
   * to the outbox and pushing it needs nothing else. Remote commands arriving
   * through `replaceState` are deliberately not announced.
   */
  observeCommands = (listener: (command: Command) => void): (() => void) => {
    this.#commandListeners.add(listener);
    return () => {
      this.#commandListeners.delete(listener);
    };
  };

  /**
   * Install — or lift — a rule that may refuse a command.
   *
   * **Settable rather than a constructor option** because the store outlives
   * identity changes: it is the object every component holds and the sync
   * client attaches to, while the binding around it is remounted when somebody
   * signs in or out (`workspace-context.tsx` says why). Signing in therefore
   * calls `setGuard(null)` on the same store the guest was using, and the
   * server takes over as the only judge.
   *
   * `dispatchBatch` and `redo` run it. `undo` and `#applyAll` deliberately do
   * not: an undo replays inverses computed against a state that has already
   * been lived in, and the one thing a guard could refuse there is the
   * `recipe.restore` that undoes a delete made *at* the cap — putting back the
   * recipe the brewer deleted a second ago, which is the count they were at
   * before. Refusing it would be technically consistent and read as the app
   * eating an undo. Plumbing the guard through history to allow exactly that
   * case is more machinery than the hole is worth, so the hole stays open and
   * is written down here.
   */
  setGuard = (guard: DispatchGuard | null): void => {
    this.#guard = guard;
  };

  /**
   * Build an envelope for `type` and apply it.
   *
   * @returns The next state, or the reducer's typed error with the state
   *   unchanged. The caller (usually a toast) decides how loudly to complain.
   */
  dispatch = <K extends CommandType>(
    type: K,
    payload: CommandOf<K>['payload'],
    options: { readonly actor?: 'user' | 'assistant' } = {},
  ): DispatchResult => {
    const command = makeCommand(type, payload, {
      commandId: this.#nextCommandId(),
      timestamp: this.#now(),
      actor: options.actor ?? 'user',
    });
    // `CommandOf<K>` for an unresolved `K` is not assignable to the `Command`
    // union — TypeScript cannot see that the mapped type covers every member.
    // The widening is sound: `makeCommand` already paired `type` and `payload`.
    return this.dispatchCommand(command as Command);
  };

  /** Apply an already-built command. */
  dispatchCommand = (command: Command): DispatchResult => this.dispatchBatch([command]);

  /**
   * Stamp envelopes onto a list of type+payload pairs and apply them as one
   * undo step. The malt-bill "% of grist" rewrite is the reason this exists
   * in the UI dispatcher: N amount updates are one thought.
   */
  dispatchAll = (steps: readonly DispatchStep[], summary?: string): DispatchResult => {
    const commands = steps.map(
      (step) =>
        makeCommand(step.type, step.payload, {
          commandId: this.#nextCommandId(),
          timestamp: this.#now(),
          actor: 'user',
        }) as Command,
    );
    return this.dispatchBatch(commands, summary);
  };

  /**
   * Apply an ordered batch atomically, as **one** undo step.
   *
   * This is the path an accepted assistant changeset takes (PLAN §7.1). Each
   * command still goes through the identical `applyCommand` a keystroke does,
   * and each is still announced to the sync engine individually — the batch is
   * an interaction-level grouping, not a second write path.
   *
   * All or nothing: if any command is refused, the state is left exactly as it
   * was and the reducer's error comes back with the offending index.
   *
   * @param commands - In application order; each sees the state the last produced.
   * @param summary - Undo label. Defaults to the first command's own summary.
   */
  dispatchBatch = (commands: readonly Command[], summary?: string): DispatchResult => {
    if (commands.length === 0) return { ok: true, state: this.#state, entryId: null };

    const before = this.#state;
    const inverses: Command[] = [];
    let projected = before;
    let undoable = true;

    for (const [index, command] of commands.entries()) {
      // Before the reducer, because a guard's question is about the workspace
      // this command would join rather than about the command's own validity.
      const refused = this.#guard?.(projected, command) ?? null;
      if (refused !== null) return { ok: false, error: { ...refused, index } };
      const inverse = invert(projected, command);
      if (inverse === null) undoable = false;
      else inverses.push(inverse);
      const result = applyCommand(projected, command);
      // Nothing has been committed yet, so a failure needs no rollback.
      if (!result.ok) return { ok: false, error: { ...result.error, index } };
      projected = result.state;
    }

    let entryId: string | null = null;
    if (undoable) {
      this.#entrySequence += 1;
      entryId = `h${this.#entrySequence}`;
      this.#undo.push({
        id: entryId,
        commands,
        inverses,
        summary: summary ?? describeCommand(before, commands[0] as Command),
      });
      if (this.#undo.length > this.#historyLimit) this.#undo.shift();
    }
    this.#redo.length = 0;
    this.#commit(projected);
    for (const command of commands) this.#announce(command);
    return { ok: true, state: projected, entryId };
  };

  /** Undo the most recent entry, replaying its inverses backwards. */
  undo = (): DispatchResult | null => {
    const entry = this.#undo.pop();
    if (entry === undefined) return null;
    // The inverses' timestamps restore the pre-state `updatedAt` and must not be
    // re-stamped; ids are refreshed only so history entries stay unique.
    const replay = [...entry.inverses]
      .reverse()
      .map((inverse) => ({ ...inverse, commandId: this.#nextCommandId() }) as Command);

    const applied = this.#applyAll(replay);
    if (!applied.ok) {
      this.#undo.push(entry);
      return applied;
    }
    this.#redo.push(entry);
    this.#commit(applied.state);
    for (const command of replay) this.#announce(command);
    return { ok: true, state: applied.state, entryId: entry.id };
  };

  /** Re-apply the most recently undone entry, and re-invert against what it saw. */
  redo = (): DispatchResult | null => {
    const entry = this.#redo.pop();
    if (entry === undefined) return null;
    const replay = entry.commands.map(
      (command) => ({ ...command, commandId: this.#nextCommandId() }) as Command,
    );

    const before = this.#state;
    const inverses: Command[] = [];
    let projected = before;
    for (const command of replay) {
      // A redo of a create *is* a create, so it meets the guard an ordinary
      // dispatch meets. The entry stays on the redo stack, unconsumed.
      const refused = this.#guard?.(projected, command) ?? null;
      if (refused !== null) {
        this.#redo.push(entry);
        return { ok: false, error: refused };
      }
      const inverse = invert(projected, command);
      if (inverse !== null) inverses.push(inverse);
      const result = applyCommand(projected, command);
      if (!result.ok) {
        this.#redo.push(entry);
        return { ok: false, error: result.error };
      }
      projected = result.state;
    }

    this.#undo.push({ ...entry, commands: replay, inverses });
    this.#commit(projected);
    for (const command of replay) this.#announce(command);
    return { ok: true, state: projected, entryId: entry.id };
  };

  /** The entry ⌘Z would undo, or `null`. */
  topUndoId = (): string | null => this.#undo.at(-1)?.id ?? null;

  /** The entry ⇧⌘Z would redo, or `null`. */
  topRedoId = (): string | null => this.#redo.at(-1)?.id ?? null;

  /** Whether an entry is still on either stack — `false` once dropped or cleared. */
  hasEntry = (id: string): boolean =>
    this.#undo.some((entry) => entry.id === id) || this.#redo.some((entry) => entry.id === id);

  /** Apply a batch to the current state without committing or announcing it. */
  #applyAll(commands: readonly Command[]): DispatchResult {
    let projected = this.#state;
    for (const command of commands) {
      const result = applyCommand(projected, command);
      if (!result.ok) return { ok: false, error: result.error };
      projected = result.state;
    }
    return { ok: true, state: projected, entryId: null };
  }

  /**
   * Replace the whole workspace — hydration from IndexedDB, and the reload
   * another tab's write triggers.
   *
   * With no `over`, history is cleared: the stacked inverses were computed
   * against a state that no longer exists, and applying one to a state it never
   * saw is exactly the corruption `invert`'s pre-state contract exists to
   * prevent.
   *
   * With `over` — the commands that are the *whole* difference between the
   * state on screen and `state` — an entry survives if none of those commands
   * writes a field it writes (see the module header). Both stacks judge an
   * entry by its commands **and** its inverses, because the store does not
   * trust a command's scope to cover its inverse: `water.clear` names only the
   * recipe's water, but it inverts to a `recipe.restore` of the whole recipe,
   * so judged by its command alone it would survive a collaborator's rename
   * and ⌘Z would write the old name back over theirs. On the redo stack the
   * inverses are the ones an undo already applied, and either side meeting the
   * remote commands means the entry was computed against something that moved.
   */
  replaceState = (
    state: WorkspaceState,
    options: { readonly over?: readonly Command[] } = {},
  ): void => {
    const over = options.over;
    if (over === undefined) {
      this.#undo.length = 0;
      this.#redo.length = 0;
    } else {
      const untouched = (entry: HistoryEntry) =>
        !commandsOverlap([...entry.commands, ...entry.inverses], over);
      retain(this.#undo, untouched);
      retain(this.#redo, untouched);
    }
    this.#commit(state);
  };

  #announce(command: Command): void {
    for (const listener of this.#commandListeners) listener(command);
  }

  #commit(state: WorkspaceState): void {
    this.#state = state;
    const undoTop = this.#undo.at(-1);
    const redoTop = this.#redo.at(-1);
    this.#history =
      undoTop === undefined && redoTop === undefined
        ? EMPTY_HISTORY
        : {
            canUndo: undoTop !== undefined,
            canRedo: redoTop !== undefined,
            undoLabel: undoTop?.summary ?? null,
            redoLabel: redoTop?.summary ?? null,
          };
    for (const listener of this.#listeners) listener();
  }
}

/** Keep, in place and in order, the entries `keep` accepts. */
function retain(entries: HistoryEntry[], keep: (entry: HistoryEntry) => boolean): void {
  const kept = entries.filter(keep);
  entries.length = 0;
  entries.push(...kept);
}
